import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Inbox, Mail, Phone, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/site";

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api
      .get("/costume-enquiries")
      .then((res) => setEnquiries(res.data))
      .catch(() => {
        setEnquiries([]);
        toast.error("Couldn't load enquiries");
      });
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (enq, status) => {
    try {
      await api.patch(`/costume-enquiries/${enq.id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === enq.id ? { ...e, status } : e)));
      toast.success(status === "handled" ? "Marked as handled" : "Moved back to new");
    } catch {
      toast.error("Couldn't update enquiry");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/costume-enquiries/${deleting.id}`);
      toast.success("Enquiry deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete enquiry");
    }
  };

  const newCount = enquiries?.filter((e) => e.status === "new").length || 0;

  return (
    <div data-testid="admin-enquiries-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Costume enquiries</h1>
          <p className="text-muted-foreground mt-1">
            Queries from the Costume Hire page.{" "}
            {enquiries && (newCount > 0 ? `${newCount} waiting for a reply.` : "All caught up!")}
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {enquiries === null ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 rounded-[4px]" />)
        ) : enquiries.length === 0 ? (
          <Card className="rounded-[4px]">
            <CardContent className="p-12 text-center" data-testid="admin-enquiries-empty">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="font-display text-2xl font-semibold mt-4">No enquiries yet</p>
              <p className="text-muted-foreground text-sm mt-2 max-w-sm mx-auto">
                When a parent sends a costume or uniform query from the website, it will land here.
              </p>
            </CardContent>
          </Card>
        ) : (
          enquiries.map((enq) => (
            <Card key={enq.id} className={`rounded-[4px] ${enq.status === "new" ? "border-[hsl(var(--kerrys-coral))]/60" : ""}`} data-testid="admin-enquiry-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Badge
                        variant="outline"
                        className={`rounded-[4px] font-semibold ${
                          enq.status === "new"
                            ? "bg-[hsl(var(--kerrys-coral))]/15 text-[hsl(18,80%,38%)] border-[hsl(var(--kerrys-coral))]/50"
                            : "bg-secondary text-muted-foreground"
                        }`}
                        data-testid="admin-enquiry-status-badge"
                      >
                        {enq.status === "new" ? "New" : "Handled"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{fmtDate(enq.created_at)}</span>
                    </div>
                    <p className="font-display text-xl sm:text-2xl font-semibold mt-2.5" data-testid="admin-enquiry-item">{enq.item}</p>
                    <p className="text-sm mt-1.5">
                      <span className="font-semibold">{enq.name}</span>
                      {enq.child_age && <span className="text-muted-foreground"> &middot; child age {enq.child_age}</span>}
                    </p>
                    {enq.message && <p className="text-sm text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">{enq.message}</p>}
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm">
                      <a href={`mailto:${enq.email}`} className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline" data-testid="admin-enquiry-email-link">
                        <Mail className="h-3.5 w-3.5" /> {enq.email}
                      </a>
                      {enq.phone && (
                        <a href={`tel:${enq.phone}`} className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline" data-testid="admin-enquiry-phone-link">
                          <Phone className="h-3.5 w-3.5" /> {enq.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 shrink-0">
                    {enq.status === "new" ? (
                      <Button size="sm" onClick={() => setStatus(enq, "handled")} className="rounded-[4px] font-semibold" data-testid="admin-enquiry-handle-button">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark handled
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(enq, "new")} className="rounded-[4px]" data-testid="admin-enquiry-reopen-button">
                        <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleting(enq)}
                      className="rounded-[4px] text-destructive hover:text-destructive"
                      data-testid="admin-enquiry-delete-button"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              The enquiry from {deleting?.name} about &ldquo;{deleting?.item}&rdquo; will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[4px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-[4px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-enquiry-confirm-delete-button">
              Delete enquiry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
